import React, { useState } from 'react';

import { Route, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useGetUsersQuery } from '@school_ify/schoolify-repository/src/teacher/types';

import StyledTooltip from 'components/StyledTooltip/StyledTooltip';
import { ReactComponent as PencilIcon } from 'assets/icons/pencil.svg';
import usePaging from 'hooks/usePaging';
import useDebounce from 'hooks/useDebounce';
import Loader from 'clean/components/common/Loader/Loader';
import Pagination from 'clean/components/common/Pagination/Pagination';

import UserDetails from '../UserDetails/UserDetails';
import RoleTag from '../RoleTag/RoleTag';

import './UserTable.scss';

interface Props {
  selectedRoleId: string;
  search: string;
}

const UserTable: React.FC<Props> = ({ selectedRoleId, search }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const searchDebounced = useDebounce(search, 200);
  const [isFirstFetch, setIsFirstFetch] = useState(true);

  const paging = usePaging({
    filters: [selectedRoleId, searchDebounced],
  });

  const {
    data,
    loading: itemsLoading,
    refetch,
    previousData,
  } = useGetUsersQuery({
    variables: {
      limit: paging.limit,
      offset: paging.offset,
    },
    onCompleted: () => {
      setIsFirstFetch(false);
      paging.setItemCount(6);
    },
    fetchPolicy: 'network-only',
  });

  const items = data || previousData;

  return (
    <div className="userTable">
      <Route path={'/permissions/users/:id'}>
        <UserDetails
          onClose={() => {
            history.push('/permissions/users');
          }}
          onSave={() => {
            refetch();
          }}
        />
      </Route>

      {isFirstFetch && itemsLoading && (
        <div className="loadingContainer">
          <Loader fade={false} />
        </div>
      )}

      {!isFirstFetch && (
        <>
          <table style={{ opacity: itemsLoading ? '0.7' : '1' }}>
            <thead>
              <tr>
                <th className="text-label">{t('permissions:UserTableRole')}</th>
                <th className="text-label" style={{ textAlign: 'left' }}>
                  {t('permissions:UserTableName')}
                </th>
                <th className="text-label" style={{ width: '99%' }}></th>
                <th className="text-label">{t('permissions:UserTableActions')}</th>
              </tr>
            </thead>
            <tbody>
              {items?.roles.map(role => {
                return (
                  <tr className={'row'} key={role.id}>
                    <td className="roleCell">
                      <RoleTag color={role.colour}>{role.title}</RoleTag>
                    </td>
                    <td className="nameCell nowrap">{role.title}</td>

                    <td></td>
                    <td>
                      <div className="iconCell">
                        <StyledTooltip
                          tagName="div"
                          direction="up"
                          content={<p>{t('permissions:UserTableEditUser')}</p>}
                        >
                          <div
                            className="actionIconContainer actionIconContainerInfo"
                            onClick={() => {
                              history.push(`/permissions/users/${role.id}`);
                            }}
                          >
                            <PencilIcon />
                          </div>
                        </StyledTooltip>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination pagingInfo={paging} />
        </>
      )}
    </div>
  );
};

export default UserTable;
